// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import LazyImage, { LazyImageProps } from "@bgs/MobiusOverrides/LazyImage/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    viewAll: any;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    headerWrapper?: InjectableCss;
    viewAllText?: InjectableCss;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    erpGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    productGridItem?: GridItemProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.regular,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        color: "text.main",
        css: css`
            letter-spacing: 0px;
            text-align: left;
        `,
    },
    headerWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            width: 100%;
            margin-bottom: 18px;
        `,
    },
    viewAllText: {
        css: css`
            max-width: 135px;
            span {
                text-transform: uppercase;
                color: ${getColor("common.ui11")};
                font-size: ${fontConfig.fontSize.xxSmall}px;
                letter-spacing: 0.86px;
                line-height: ${fontConfig.lineHeight.singleLineSmall};
                font-weight: ${fontConfig.weight.semiBold};
            }
            &:hover,
            &:focus {
                text-decoration: underline;
            }
        `,
    },
    productGridItem: {
        width: [12, 6, 4, 3, 2.4] as any,
        css: css`
            margin-bottom: 6px !important;

            &:hover,
            &:focus {
                box-shadow: 0 2px 11px 0 rgba(0, 0, 0, 0.2);
                cursor: pointer;
            }
        `,
    },
    imageGridItem: {
        width: 12,
        css: css`
            > div {
                width: 100%;
                height: 155px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: auto;
                cursor: pointer;
                img {
                    max-width: 155px;
                    max-height: 155px;
                }
            }
        `,
    },
    image: {},
    infoGridItem: {
        width: 12,
        css: css`
            height: 66px;
            margin-bottom: -5px;
            display: flex;
            justify-content: center;
        `,
    },
    titleLink: {
        typographyProps: {
            css: css`
                max-width: 153px;
                &,
                * {
                    letter-spacing: 1px;
                    text-align: left;
                    color: ${getColor("text.main")};
                    font-size: ${fontConfig.fontSize.small}px;
                    font-weight: ${fontConfig.weight.semiBold};
                    text-transform: uppercase;
                }
            `,
        },
    },
    erpGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    erpNumberText: {},
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, viewAll } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.headerWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Top Product Results")}
                    </Typography>
                </StyledWrapper>
                <GridContainer gap={10}>
                    {products.map(
                        (product, index) =>
                            index < 7 && (
                                <GridItem
                                    key={product.id as string}
                                    onClick={() => {
                                        this.props.goToUrl(product.url);
                                    }}
                                    {...styles.productGridItem}
                                >
                                    <StyledWrapper
                                        css={css`
                                            width: 100%;
                                            height: 100%;
                                        `}
                                    >
                                        <GridContainer css={css``} gap={10} className="autocomplete-products-container">
                                            <GridItem
                                                {...styles.imageGridItem}
                                                className="autocomplete-products-image-grid-item"
                                            >
                                                <LazyImage
                                                    {...styles.image}
                                                    height="170"
                                                    width="170"
                                                    cloudinaryWidth="170"
                                                    altText={product.title || product.name}
                                                    src={product.image}
                                                    className="autocomplete-products-image"
                                                />
                                            </GridItem>
                                            <GridItem
                                                {...styles.infoGridItem}
                                                className="autocomplete-products-info-grid-item"
                                            >
                                                <Link
                                                    {...styles.titleLink}
                                                    data-test-selector={`productId_${product.id}`}
                                                    className="autocomplete-products-link"
                                                >
                                                    <Truncate lines={3}>{product.title}</Truncate>
                                                </Link>
                                            </GridItem>
                                            <GridItem {...styles.erpGridItem}>
                                                <StyledWrapper
                                                    css={css`
                                                        max-width: 153px;
                                                        min-width: 153px;
                                                    `}
                                                    data-test-selector="autocompleteProducts_PartNumber"
                                                    className="autocomplete-products-erp-number"
                                                >
                                                    <Typography
                                                        size={12}
                                                        lineHeight="20px"
                                                        weight={fontConfig.weight.semiBold}
                                                        css={css`
                                                            letter-spacing: 0;
                                                        `}
                                                        color="text.main"
                                                    >
                                                        {translate("MFR Part #:")}&nbsp;
                                                    </Typography>
                                                    <Typography size={12} weight={fontConfig.weight.light}>
                                                        {product.manufacturerItemNumber}
                                                    </Typography>
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </StyledWrapper>
                                </GridItem>
                            ),
                    )}
                    <GridItem
                        {...styles.productGridItem}
                        css={css`
                            min-height: 260px;
                            max-height: 260px;

                            display: flex;
                            align-items: center;
                            justify-content: center;
                            text-align: center;
                            margin-left: 10px;
                            ${xScreen("lte", "sm")} {
                                justify-content: center;
                            }
                        `}
                    >
                        <StyledWrapper
                            css={css`
                                display: flex;
                                width: 100%;
                                height: 100%;
                                max-width: 170px !important;
                                justify-content: center;
                                background: ${getColor("common.ui4")};
                            `}
                        >
                            <Link
                                aria-label={translate("view all results")}
                                {...styles.viewAllText}
                                onClick={viewAll}
                                color="primary"
                            >
                                {translate("view all products result")}
                            </Link>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </>
        );
    }
}

export default AutocompleteProducts;
